// Copied verbatim from the browser extension (browser-fence-2: src/shared/fency) so the
// dashboard and the extension show the same Fency. Keep both copies in sync.
// The 'sleepy' and 'thinking' moods and lookAt() were added here first (the dashboard's
// companion uses them): port them to the extension's copy.

import frontSvg from './art/fency-front.svg?raw';
import pointSvg from './art/fency-point.svg?raw';
import thumbsSvg from './art/fency-thumbs.svg?raw';
import headSvg from './art/fency-head.svg?raw';

// Fency, alive. A small animation engine in the style of the fency-demo brand film: every
// frame is computed from the clock (hover, breathing, sway), with behaviour on top that
// never loops the same way twice: blinks and glances at random intervals, eyes that follow
// the pointer, idle gestures (a hop, a curious tilt, a look around), a "talking" mode and
// a happy squint. Plain DOM + inline SVG, so the same code runs in the popup (Vue) and in
// the Shadow DOM of pages. No bitmaps and no stylesheet, so a page's CSP can't break it.

export type FencyPose = 'front' | 'point' | 'thumbs' | 'head';
// alert: something happened that the user must see: a startled jump, wide eyes looking
// straight at them, visor lights flashing together like a warning light.
// sleepy: dozing when nobody is around: eyes nearly shut, slow deep breaths, a nodding
// head, visor lights dimmed to a slow glow.
// thinking: working something out: eyes up and to the side, a slight tilt, visor lights
// sweeping fast.
export type FencyMood = 'idle' | 'talking' | 'happy' | 'alert' | 'sleepy' | 'thinking';

export interface FencyCue {
  at: number; // seconds after mount
  pose?: FencyPose;
  mood?: FencyMood;
  hop?: boolean;
}

export interface FencyOptions {
  pose: FencyPose;
  // Rendered height of the figure in px.
  height: number;
  // Every pose this Fency may switch to, so the box can fit them all (default: [pose]).
  poses?: FencyPose[];
  // 'body': the box is the body, centred on it (arms may reach outside).
  // 'all': the box contains every pose in `poses`, arms included.
  fit?: 'body' | 'all';
  // Fly up into place on mount (demo: outBack with a slight tilt).
  entrance?: boolean;
  // Ground shadow and brand spotlight behind the figure.
  ground?: boolean;
  // Eyes follow the pointer; a click on Fency makes it hop happily.
  interactive?: boolean;
  // A little timeline, like the demo's scenes: e.g. turn to point once it has landed.
  cues?: FencyCue[];
}

interface Art {
  svg: string;
  w: number; // viewBox width
  h: number; // viewBox height
  cx: number; // x of the body's centre line, in viewBox units
  eyeY: number; // eye height as a fraction of h (where the gaze originates)
  look: [number, number]; // max eye travel, viewBox units
}

const ART: Record<FencyPose, Art> = {
  front: { svg: frontSvg, w: 350, h: 482, cx: 175, eyeY: 0.3, look: [16, 9] },
  point: { svg: pointSvg, w: 497.38, h: 486, cx: 150, eyeY: 0.28, look: [15, 8] },
  thumbs: { svg: thumbsSvg, w: 507.26, h: 480, cx: 176, eyeY: 0.3, look: [16, 9] },
  head: { svg: headSvg, w: 200, h: 170, cx: 100, eyeY: 0.57, look: [11, 7] },
};

interface Layer {
  pose: FencyPose;
  el: HTMLElement;
  eyes: SVGGElement[];
  dots: SVGGElement[];
}

// ── easing (same curves as the demo) ─────────────────────────────────────────────
const clamp = (x: number, a = 0, b = 1) => Math.min(b, Math.max(a, x));
const outBack = (x: number) => {
  const c1 = 1.7;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
};
const bump = (x: number) => (x > 0 && x < 1 ? Math.sin(Math.PI * x) : 0);
const rand = (a: number, b: number) => a + Math.random() * (b - a);

const HOP_S = 0.5;
const LAND_S = 0.16;
const BLINK_S = 0.16;
const TURN_S = 0.34;

export class Fency {
  public readonly element: HTMLElement;

  private opts: Required<Omit<FencyOptions, 'cues'>> & { cues: FencyCue[] };
  private mover: HTMLElement;
  private shadow: HTMLElement | null = null;
  private layers = new Map<FencyPose, Layer>();
  private current: Layer;
  private previous: Layer | null = null;
  private bodyX: number;
  private k: number; // motion scale: 1 at a 120px tall figure

  private start = performance.now();
  private last = this.start;
  private raf = 0;
  private wasConnected = false;
  private reduced = false;
  private motionQuery: MediaQueryList | null = null;

  // behaviour state (all times in seconds since start)
  private mood: FencyMood = 'idle';
  private happy = 0;
  private happyUntil = -1;
  private sleep = 0;
  private think = 0;
  // Where the host wants the eyes (-1..1 each way), over everything but alert.
  private look: { x: number; y: number } | null = null;
  private gx = 0;
  private gy = 0;
  private tx = 0;
  private ty = 0;
  private nextSaccade = 0.6;
  private pointer: { x: number; y: number; at: number } | null = null;
  private nextBlink = rand(0.9, 2.2);
  private blinkAt = -10;
  private hopAt = -10;
  private tiltAt = -10;
  private tiltDir = 1;
  private lookAroundAt = -10;
  private nextGesture = rand(3.5, 6);
  private turnAt = -10;
  private cueIndex = 0;

  constructor(options: FencyOptions) {
    // `??` rather than spreading, so an explicit `undefined` (e.g. an unset Vue prop)
    // still gets the default.
    this.opts = {
      pose: options.pose,
      height: options.height,
      poses: options.poses ?? [options.pose],
      fit: options.fit ?? 'body',
      entrance: options.entrance ?? true,
      ground: options.ground ?? options.pose !== 'head',
      interactive: options.interactive ?? true,
      cues: [...(options.cues ?? [])].sort((a, b) => a.at - b.at),
    };
    this.k = this.opts.height / 120;

    const { width, bodyX } = this.layout();
    this.bodyX = bodyX;

    this.element = document.createElement('div');
    this.element.setAttribute('aria-hidden', 'true');
    Object.assign(this.element.style, {
      position: 'relative',
      display: 'inline-block',
      flexShrink: '0',
      width: `${width}px`,
      height: `${this.opts.height}px`,
      userSelect: 'none',
      cursor: this.opts.interactive ? 'pointer' : 'default',
    });

    if (this.opts.ground) this.addGround();

    this.mover = document.createElement('div');
    Object.assign(this.mover.style, {
      position: 'absolute',
      inset: '0',
      transformOrigin: `${bodyX}px 100%`,
      willChange: 'transform',
      opacity: this.opts.entrance ? '0' : '1',
    });
    this.element.appendChild(this.mover);

    this.current = this.layer(this.opts.pose);
    this.current.el.style.opacity = '1';

    if (this.opts.interactive) {
      window.addEventListener('pointermove', this.onPointerMove, { passive: true });
      this.element.addEventListener('pointerdown', this.onPoke);
    }

    if (typeof matchMedia === 'function') {
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.reduced = this.motionQuery.matches;
      this.motionQuery.addEventListener?.('change', this.onMotionChange);
    }

    this.raf = requestAnimationFrame(this.frame);
  }

  // ── public API ─────────────────────────────────────────────────────────────────

  public setPose(pose: FencyPose): void {
    if (pose === this.current.pose) return;
    const next = this.layer(pose);
    if (this.previous && this.previous !== next) this.previous.el.style.opacity = '0';
    this.previous = this.current;
    this.current = next;
    this.turnAt = this.now();
  }

  public setMood(mood: FencyMood): void {
    if (mood === 'alert' && this.mood !== 'alert') this.hop(); // startled
    this.mood = mood;
  }

  // Points the eyes somewhere (-1..1 on each axis, e.g. at a field being typed in, or
  // away from a password); null hands the gaze back to Fency.
  public lookAt(target: { x: number; y: number } | null): void {
    this.look = target ? { x: clamp(target.x, -1, 1), y: clamp(target.y, -1, 1) } : null;
  }

  public hop(): void {
    const t = this.now();
    if (t - this.hopAt > HOP_S + LAND_S) this.hopAt = t;
  }

  public destroy(): void {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('pointermove', this.onPointerMove);
    this.element.removeEventListener('pointerdown', this.onPoke);
    this.motionQuery?.removeEventListener?.('change', this.onMotionChange);
  }

  // ── construction ───────────────────────────────────────────────────────────────

  private px(pose: FencyPose) {
    const art = ART[pose];
    const scale = this.opts.height / art.h;
    return { scale, width: art.w * scale, cx: art.cx * scale };
  }

  private layout(): { width: number; bodyX: number } {
    const poses = Array.from(new Set([this.opts.pose, ...this.opts.poses]));
    if (this.opts.fit === 'all') {
      let minLeft = 0;
      let maxRight = 0;
      for (const pose of poses) {
        const { width, cx } = this.px(pose);
        minLeft = Math.min(minLeft, -cx);
        maxRight = Math.max(maxRight, width - cx);
      }
      return { width: maxRight - minLeft, bodyX: -minLeft };
    }
    // Body box: the head is the widest part of the body, ~72% of the figure's height.
    const width = this.opts.pose === 'head' ? this.px('head').width : this.opts.height * 0.72;
    return { width, bodyX: width / 2 };
  }

  private addGround(): void {
    const h = this.opts.height;

    const spot = document.createElement('div');
    Object.assign(spot.style, {
      position: 'absolute',
      width: `${h * 1.15}px`,
      height: `${h * 1.15}px`,
      left: `${this.bodyX - h * 0.575}px`,
      top: `${-h * 0.1}px`,
      borderRadius: '50%',
      pointerEvents: 'none',
      background:
        'radial-gradient(closest-side, rgba(91, 65, 245, 0.28) 0%, rgba(91, 65, 245, 0.1) 55%, transparent 100%)',
    });
    this.element.appendChild(spot);

    this.shadow = document.createElement('div');
    Object.assign(this.shadow.style, {
      position: 'absolute',
      bottom: '-5px',
      height: '9px',
      width: `${h * 0.4}px`,
      left: `${this.bodyX - h * 0.2}px`,
      borderRadius: '50%',
      pointerEvents: 'none',
      background: 'radial-gradient(closest-side, rgba(8, 16, 24, 0.38), transparent)',
    });
    this.element.appendChild(this.shadow);
  }

  private layer(pose: FencyPose): Layer {
    const existing = this.layers.get(pose);
    if (existing) return existing;

    const { width, cx } = this.px(pose);
    const el = document.createElement('div');
    el.innerHTML = ART[pose].svg;
    Object.assign(el.style, {
      position: 'absolute',
      bottom: '0',
      left: `${this.bodyX - cx}px`,
      width: `${width}px`,
      height: `${this.opts.height}px`,
      opacity: '0',
      transformOrigin: `${cx}px 60%`,
    });

    const svg = el.querySelector('svg');
    if (svg) {
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.style.display = 'block';
      svg.style.overflow = 'visible';
    }

    const prep = (g: SVGGElement) => {
      g.style.transformBox = 'fill-box';
      g.style.transformOrigin = 'center';
      return g;
    };
    const layer: Layer = {
      pose,
      el,
      eyes: Array.from(el.querySelectorAll<SVGGElement>('[data-fx="eye"]')).map(prep),
      dots: Array.from(el.querySelectorAll<SVGGElement>('[data-fx="dot"]')),
    };

    this.mover.appendChild(el);
    this.layers.set(pose, layer);
    return layer;
  }

  // ── input ──────────────────────────────────────────────────────────────────────

  private onPointerMove = (event: PointerEvent) => {
    this.pointer = { x: event.clientX, y: event.clientY, at: this.now() };
  };

  private onPoke = () => {
    this.hop();
    this.happyUntil = this.now() + 1.4;
  };

  private onMotionChange = (event: MediaQueryListEvent) => {
    this.reduced = event.matches;
  };

  private now(): number {
    return (performance.now() - this.start) / 1000;
  }

  // ── behaviour ──────────────────────────────────────────────────────────────────

  private runCues(t: number): void {
    while (this.cueIndex < this.opts.cues.length && this.opts.cues[this.cueIndex].at <= t) {
      const cue = this.opts.cues[this.cueIndex++];
      if (cue.pose) this.setPose(cue.pose);
      if (cue.mood) this.setMood(cue.mood);
      if (cue.hop) this.hop();
    }
  }

  private gesture(t: number): void {
    if (t < this.nextGesture) return;
    this.nextGesture = t + rand(4.5, 9);
    if (this.mood === 'sleepy') return; // no blinking in its sleep
    if (this.mood === 'talking' || this.mood === 'alert' || this.mood === 'thinking' || this.reduced) {
      this.nextBlink = t; // the only "gesture" that isn't motion: a blink
      return;
    }
    const roll = Math.random();
    if (roll < 0.3) this.hop();
    else if (roll < 0.6) this.lookAroundAt = t;
    else if (roll < 0.85) {
      this.tiltAt = t;
      this.tiltDir = Math.random() < 0.5 ? -1 : 1;
    } else this.nextBlink = t; // a (possibly double) blink
  }

  private gaze(t: number): void {
    const rect = this.pointer ? this.element.getBoundingClientRect() : null;
    const lookAround = t - this.lookAroundAt;

    if (this.mood === 'alert') {
      // Looks straight at the user, with a slight nervous tremble.
      this.tx = 0.08 * Math.sin(t * 7);
      this.ty = 0.15;
    } else if (this.look) {
      this.tx = this.look.x;
      this.ty = this.look.y;
    } else if (this.mood === 'sleepy') {
      this.tx = 0;
      this.ty = 0.35; // head down
    } else if (this.mood === 'thinking') {
      // Up and to the side, drifting a little: "hmm, let me see".
      this.tx = 0.65 + 0.15 * Math.sin(t * 1.3);
      this.ty = -0.85;
    } else if (this.mood === 'talking') {
      // Looks at its speech bubble (to its right), with small lively corrections.
      this.tx = 0.85 + 0.1 * Math.sin(t * 3.1);
      this.ty = 0.1 + 0.1 * Math.sin(t * 2.3);
    } else if (lookAround >= 0 && lookAround < 1.8) {
      // Glances left, then right, then back: "is everything fine around here?"
      const p = lookAround / 1.8;
      this.tx = p < 0.35 ? -1 : p < 0.75 ? 1 : 0;
      this.ty = -0.2;
    } else if (rect && this.pointer && t - this.pointer.at < 2.5) {
      // Follows the pointer.
      const ex = rect.left + this.bodyX;
      const ey = rect.top + rect.height * ART[this.current.pose].eyeY;
      this.tx = clamp((this.pointer.x - ex) / 160, -1, 1);
      this.ty = clamp((this.pointer.y - ey) / 160, -1, 1);
    } else if (t >= this.nextSaccade) {
      // Idle: quick glances to random spots, often back to the centre.
      const centre = Math.random() < 0.35;
      this.tx = centre ? 0 : rand(-1, 1);
      this.ty = centre ? 0 : rand(-0.7, 0.5);
      this.nextSaccade = t + rand(0.6, 2.4);
    }
  }

  private blink(t: number): number {
    if (t >= this.nextBlink && this.mood !== 'sleepy') {
      this.blinkAt = t;
      // A quarter of blinks are doubles.
      this.nextBlink = t + (Math.random() < 0.25 ? 0.28 : rand(1.8, 5));
    }
    return 1 - bump((t - this.blinkAt) / BLINK_S);
  }

  // ── frame ──────────────────────────────────────────────────────────────────────

  private frame = (now: number) => {
    if (this.element.isConnected) this.wasConnected = true;
    else if (this.wasConnected) {
      // Removed from the page: stop for good.
      this.destroy();
      return;
    }

    const t = (now - this.start) / 1000;
    const dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    const k = this.k;
    const still = this.reduced;

    this.runCues(t);
    this.gesture(t);
    this.gaze(t);

    // Eyes ease toward the target fast, like a saccade.
    const follow = 1 - Math.exp(-dt * 16);
    this.gx += (this.tx - this.gx) * follow;
    this.gy += (this.ty - this.gy) * follow;

    const wantHappy = this.mood === 'happy' || t < this.happyUntil ? 1 : 0;
    this.happy += (wantHappy - this.happy) * (1 - Math.exp(-dt * 10));
    // Dozing off is slow, waking up quick.
    const wantSleep = this.mood === 'sleepy' && wantHappy === 0 ? 1 : 0;
    this.sleep += (wantSleep - this.sleep) * (1 - Math.exp(-dt * (wantSleep ? 1.5 : 8)));
    const wantThink = this.mood === 'thinking' ? 1 : 0;
    this.think += (wantThink - this.think) * (1 - Math.exp(-dt * 6));

    // ── body: entrance, hover, breathing, sway, gestures ──
    const enter = this.opts.entrance && !still ? outBack(clamp(t / 0.75)) : 1;
    let y = 0;
    let rot = 0;
    let sx = 1;
    let sy = 1;

    if (!still) {
      const awake = 1 - 0.7 * this.sleep;
      y -= (Math.sin(t * 2.4) * 5 + Math.sin(t * 1.13 + 1) * 2) * k * awake; // hover
      y += this.sleep * 4 * k; // sinks a little when dozing
      const breath = 0.014 + 0.022 * this.sleep; // deeper breaths asleep
      sy *= 1 + breath * Math.sin(t * 2.4 - 0.6); // breathing
      sx *= 1 - breath * 0.45 * Math.sin(t * 2.4 - 0.6);
      rot += Math.sin(t * 0.9) * 1.6 * awake + this.gx * 3.5; // sway, and lean into the gaze
      rot += this.sleep * (4 + 3 * Math.sin(t * 0.7)); // nodding off
      rot += this.think * (5 + 1.5 * Math.sin(t * 1.7)); // a thoughtful tilt

      if (this.mood === 'talking') rot += Math.sin(t * 8.5) * 1.3; // nodding along

      const tilt = t - this.tiltAt;
      if (tilt >= 0 && tilt < 1.4) rot += this.tiltDir * 7 * bump(tilt / 1.4); // curious tilt

      const hop = t - this.hopAt;
      if (hop >= 0 && hop < HOP_S) {
        y -= 20 * k * bump(hop / HOP_S);
        sy *= 1 + 0.06 * bump(hop / HOP_S); // stretch in the air
        sx *= 1 - 0.04 * bump(hop / HOP_S);
      } else if (hop >= HOP_S && hop < HOP_S + LAND_S) {
        const land = bump((hop - HOP_S) / LAND_S); // squash on landing
        sy *= 1 - 0.09 * land;
        sx *= 1 + 0.07 * land;
      }

      const turn = t - this.turnAt;
      if (turn >= 0 && turn < TURN_S) sx *= 1 - 0.12 * bump(turn / TURN_S); // turning squeeze

      y += (1 - enter) * 60 * k;
      rot += (1 - enter) * -8;
    }

    this.mover.style.opacity = String(this.opts.entrance && !still ? clamp(t / 0.3) : 1);
    this.mover.style.transform = `translateY(${y}px) rotate(${rot}deg) scale(${sx}, ${sy})`;

    if (this.shadow) {
      const lift = clamp(-y / (26 * k), 0, 1);
      this.shadow.style.transform = `scaleX(${1 - 0.3 * lift})`;
      this.shadow.style.opacity = String((1 - 0.5 * lift) * clamp(t / 0.6));
    }

    // ── pose crossfade: incoming fades in first, outgoing after, never see-through ──
    if (this.previous) {
      const p = clamp((t - this.turnAt) / TURN_S);
      this.current.el.style.opacity = String(clamp(p * 2));
      this.previous.el.style.opacity = String(clamp(2 - p * 2));
      if (p >= 1) {
        this.previous.el.style.opacity = '0';
        this.previous = null;
      }
    }

    // ── face: gaze, blink, happy squint, visor lights ──
    const [lx, ly] = ART[this.current.pose].look;
    const wide = this.mood === 'alert' ? 1.15 : 1; // eyes wide open when alarmed
    const open =
      Math.max(0.08, this.blink(t) * (1 - 0.5 * this.happy) * (1 - 0.88 * this.sleep)) * wide;
    const eyeTransform = `translate(${this.gx * lx}px, ${this.gy * ly - this.happy * 6}px) scale(${wide}, ${open})`;
    for (const layer of [this.current, this.previous]) {
      if (!layer) continue;
      for (const eye of layer.eyes) eye.style.transform = eyeTransform;
      layer.dots.forEach((dot, i) => {
        let o: number;
        if (this.happy > 0.5) o = 1;
        else if (this.mood === 'alert')
          o = t % 0.5 < 0.25 ? 1 : 0.2; // warning-light flash
        else if (this.mood === 'talking')
          o = 0.3 + 0.7 * bump((((t / 0.55 - i * 0.22) % 1) + 1) % 1);
        else if (this.mood === 'thinking')
          o = 0.2 + 0.8 * bump((((t / 0.42 - i * 0.33) % 1) + 1) % 1); // fast sweep
        else if (this.mood === 'sleepy')
          o = 0.1 + 0.22 * (0.5 + 0.5 * Math.sin(t * 1.1)); // dim, slow glow
        else o = 0.35 + 0.65 * bump(((t % 2.8) - i * 0.16) / 0.5); // idle heartbeat
        dot.style.opacity = String(o);
      });
    }

    this.raf = requestAnimationFrame(this.frame);
  };
}
